import React, { useEffect, useState } from 'react';
import { X, Save, Activity, Loader2, User, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ObjectHistory from './ObjectHistory';
import AppointmentsEditor from './AppointmentsEditor';
import MemberApplicationSection, { emptyApplication } from './MemberApplicationSection';
import { Input } from './ui/input';
import HintInput from './HintInput';
import { formatApiError } from '../lib/apiError';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const FIELDS = ['first_name', 'last_name', 'email', 'member_class', 'status', 'rank', 'unit',
  'years_served', 'address', 'suburb', 'postcode', 'phone', 'mobile', 'date_last_paid', 'note', 'next_of_kin', 'nok_relationship', 'nok_phone', 'sms_opt_out', 'birthday', 'birthday_opt_out', 'date_joined'];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs mb-1 font-title" style={{ color: 'var(--olive)' }}>{label}</span>
      {children}
    </label>
  );
}

export default function MemberEditModal({ user, memberClasses, onClose, onSaved }) {
  const { updateUserFields, getMemberActivity, user: me } = useAuth();
  const [form, setForm] = useState({});
  const [payments, setPayments] = useState([]); // [{year, amount}]
  const [appointments, setAppointments] = useState(user.appointments || []);
  const [app, setApp] = useState(() => ({ ...emptyApplication(), ...(user.application || {}) }));
  const [membershipNo, setMembershipNo] = useState(user.membership_no || '');
  const [newYear, setNewYear] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    const init = {};
    FIELDS.forEach((f) => { init[f] = user[f] || ''; });
    init.sms_opt_out = !!user.sms_opt_out;
    init.member_class = user.memberClass || user.member_class || 'contact';
    setForm(init);
    const yp = user.yearly_payments || {};
    setPayments(Object.keys(yp).sort().map((y) => ({ year: y, amount: String(yp[y]) })));
    let alive = true;
    setActivityLoading(true);
    getMemberActivity(user.user_id)
      .then((a) => { if (alive) setActivity(a); })
      .catch(() => { if (alive) setActivity({ error: true }); })
      .finally(() => { if (alive) setActivityLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.user_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [dismissedPrompt, setDismissedPrompt] = useState('');
  const statusClass = (() => { const st = (form.status || '').toLowerCase(); return st.includes('deceased') ? 'deceased' : st.includes('resigned') ? 'resigned' : /no.?contact/.test(st) ? 'no-contact' : ''; })();
  const classPrompt = statusClass && form.member_class !== statusClass && dismissedPrompt !== statusClass && memberClasses.some((mc) => mc.key === statusClass) ? statusClass : '';

  const addPayment = () => {
    if (!newYear.trim()) return;
    setPayments((p) => [...p.filter((x) => x.year !== newYear.trim()), { year: newYear.trim(), amount: newAmount.trim() }]);
    setNewYear(''); setNewAmount('');
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const yearly = {};
      payments.forEach((p) => { if (p.year) yearly[p.year] = p.amount; });
      const payload = { ...form, yearly_payments: yearly, appointments, application: app, membership_no: membershipNo };
      const newEmail = (form.email || '').trim().toLowerCase();
      if (!newEmail || newEmail === (user.email || '').toLowerCase()) delete payload.email;
      await updateUserFields(user.user_id, payload);
      setMsg({ ok: true, text: 'Member saved.' });
      if (onSaved) onSaved();
      setTimeout(() => { onClose(); }, 700);
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e, 'Could not save member.') });
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="member-edit-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}>
            <User size={18} /> Edit member
          </h3>
          <button onClick={onClose} data-testid="member-edit-close" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Details */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First name"><Input data-testid="member-edit-first-name" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} /></Field>
            <Field label="Last name"><Input data-testid="member-edit-last-name" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} /></Field>
            <Field label="Email (login)"><Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" data-testid="member-edit-email" /></Field>
            <Field label="Member class">
              <Select value={form.member_class} onValueChange={(v) => set('member_class', v)}>
                <SelectTrigger data-testid="member-edit-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {memberClasses.map((mc) => <SelectItem key={mc.key} value={mc.key}>{mc.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status"><HintInput data-testid="member-edit-status" value={form.status || ''} onChange={(e) => set('status', e.target.value)} hint="e.g. Financial, Life, Honorary" /></Field>
            {classPrompt && (
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2 px-3 py-2 rounded text-xs" style={{ background: '#fff4e5', color: '#8a5a00' }} data-testid="member-edit-class-prompt">
                <span>Status says <b>{memberClasses.find((mc) => mc.key === classPrompt)?.label}</b> — switch the member class to match?</span>
                <button type="button" onClick={() => set('member_class', classPrompt)} className="px-2.5 py-1 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="member-edit-class-prompt-switch">Switch class</button>
                <button type="button" onClick={() => setDismissedPrompt(classPrompt)} className="underline" data-testid="member-edit-class-prompt-keep">Keep {memberClasses.find((mc) => mc.key === form.member_class)?.label || 'current'}</button>
              </div>
            )}
            <Field label="Date last paid"><HintInput data-testid="member-edit-date-last-paid" value={form.date_last_paid || ''} onChange={(e) => set('date_last_paid', e.target.value)} hint="e.g. 2025-07-01" /></Field>
            <Field label="Rank"><HintInput data-testid="member-edit-rank" value={form.rank || ''} onChange={(e) => set('rank', e.target.value)} hint="e.g. Cpl" /></Field>
            <Field label="Unit"><HintInput data-testid="member-edit-unit" value={form.unit || ''} onChange={(e) => set('unit', e.target.value)} hint="e.g. 12 Fd Sqn" /></Field>
            <Field label="Birthday"><Input type="date" data-testid="member-edit-birthday" value={form.birthday || ''} onChange={(e) => set('birthday', e.target.value)} /><label className="mt-1 inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--olive)' }}><input type="checkbox" checked={!!form.birthday_opt_out} onChange={(e) => set('birthday_opt_out', e.target.checked)} data-testid="member-edit-birthday-opt-out" /> No birthday greeting email</label></Field>
            <Field label="Date first joined"><Input type="date" data-testid="member-edit-date-joined" value={form.date_joined || ''} onChange={(e) => set('date_joined', e.target.value)} /></Field>
            <Field label="Years served"><HintInput data-testid="member-edit-years" value={form.years_served || ''} onChange={(e) => set('years_served', e.target.value)} hint="e.g. 1978-1985" /></Field>
            <Field label="Phone"><Input data-testid="member-edit-phone" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
            <Field label="Mobile"><Input data-testid="member-edit-mobile" value={form.mobile || ''} onChange={(e) => set('mobile', e.target.value)} /></Field>
            <Field label="Suburb"><Input data-testid="member-edit-suburb" value={form.suburb || ''} onChange={(e) => set('suburb', e.target.value)} /></Field>
            <Field label="Postcode"><Input data-testid="member-edit-postcode" value={form.postcode || ''} onChange={(e) => set('postcode', e.target.value)} /></Field>
            <div className="sm:col-span-2">
              <Field label="Address"><Input data-testid="member-edit-address" value={form.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
            </div>
            <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3 rounded p-3" style={{ background: 'rgba(0,0,0,0.03)' }} data-testid="member-edit-nok">
              <Field label="Next of kin"><Input data-testid="member-edit-nok-name" value={form.next_of_kin || ''} onChange={(e) => set('next_of_kin', e.target.value)} /></Field>
              <Field label="Relationship"><HintInput data-testid="member-edit-nok-rel" value={form.nok_relationship || ''} onChange={(e) => set('nok_relationship', e.target.value)} hint="e.g. Spouse" /></Field>
              <Field label="NOK phone"><Input data-testid="member-edit-nok-phone" value={form.nok_phone || ''} onChange={(e) => set('nok_phone', e.target.value)} /></Field>
            </div>
            <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm"><input type="checkbox" data-testid="member-edit-sms-opt-out" checked={!!form.sms_opt_out} onChange={(e) => set('sms_opt_out', e.target.checked)} /> Opted out of SMS (no text messages)</label>
            <div className="sm:col-span-2">
              <Field label="Note"><Textarea data-testid="member-edit-note" value={form.note || ''} onChange={(e) => set('note', e.target.value)} className="h-20" /></Field>
            </div>
          </div>

          <MemberApplicationSection user={user} app={app} setApp={setApp} memberForm={form} membershipNo={membershipNo} setMembershipNo={setMembershipNo} />

          <AppointmentsEditor value={appointments} onChange={setAppointments} readOnly={!(me && me.is_executive)} />

          {/* Yearly payments */}
          <div>
            <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Yearly payments</h4>
            <div className="space-y-2" data-testid="member-edit-payments">
              {payments.length === 0 && <p className="text-xs" style={{ color: 'var(--olive)' }}>No payment years recorded.</p>}
              {payments.map((p, i) => (
                <div key={p.year} className="flex items-center gap-2">
                  <span className="text-sm font-title w-20" style={{ color: 'var(--ink)' }}>{p.year}</span>
                  <Input value={p.amount} onChange={(e) => setPayments((arr) => arr.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="h-8 w-40" data-testid={`member-edit-payment-${p.year}`} />
                  <button onClick={() => setPayments((arr) => arr.filter((_, j) => j !== i))} title="Remove year" data-testid={`member-edit-payment-del-${p.year}`}><Trash2 size={14} style={{ color: '#8a1f11' }} /></button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Input value={newYear} onChange={(e) => setNewYear(e.target.value)} placeholder="Year" className="h-8 w-20" data-testid="member-edit-payment-year" />
                <Input value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="Amount / note" className="h-8 w-40" data-testid="member-edit-payment-amount" />
                <button onClick={addPayment} className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="member-edit-payment-add"><Plus size={13} /> Add</button>
              </div>
            </div>
          </div>

          {/* Activity logs */}
          <div>
            <h4 className="font-title text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--navy)' }}><Activity size={15} /> Activity &amp; logs</h4>
            <div className="rounded border p-3" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="member-edit-activity">
              {activityLoading ? (
                <p className="text-sm flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={14} /> Loading activity…</p>
              ) : !activity || activity.error ? (
                <p className="text-sm" style={{ color: '#8a1f11' }}>Could not load activity.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-6">
                    <div><div className="text-xl font-title" style={{ color: 'var(--maroon)' }} data-testid="member-edit-activity-total">{activity.total_views}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Total page views</div></div>
                    <div><div className="text-sm font-title" style={{ color: 'var(--navy)' }}>{activity.last_seen ? new Date(activity.last_seen).toLocaleString() : 'Never'}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Last seen</div></div>
                  </div>
                  {activity.total_views === 0 ? (
                    <p className="text-sm" style={{ color: 'var(--olive)' }}>No tracked activity yet for this member.</p>
                  ) : (
                    <div>
                      <div className="text-xs font-title mb-1" style={{ color: 'var(--navy)' }}>Recent visits</div>
                      <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                        {(activity.recent || []).map((r, i) => (
                          <div key={i} className="flex justify-between gap-2" style={{ color: 'var(--ink)' }}>
                            <span className="truncate" title={r.path}>{r.label || r.path}</span>
                            <span className="whitespace-nowrap" style={{ color: 'var(--olive)' }}>{new Date(r.created_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <ObjectHistory type="member" id={user.user_id} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t sticky bottom-0 bg-white" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          {msg && <span className="text-sm mr-auto" data-testid="member-edit-msg" style={{ color: msg.ok ? '#1c6b2c' : '#8a1f11' }}>{msg.text}</span>}
          <button onClick={onClose} className="px-4 py-2 rounded text-sm" style={{ border: '1px solid rgba(0,0,0,0.15)' }} data-testid="member-edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }} data-testid="member-edit-save">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
